import type { DateTimeSettings } from '@cdw-ae/common-data';
import { DateTime } from 'luxon';

// ========================================
// Private Helper Functions
// ========================================

function getDateFormatString(format: string): string {
  const formats: Record<string, string> = {
    US: 'MM/dd/yy',
    CANADIAN: 'yyyy-MM-dd',
  };
  return formats[format] || 'MM/dd/yy';
}

function getTimeFormat(format: string): string {
  return format === '24h' ? 'HH:mm' : 'h:mm a';
}

function getFormatString(dateFormat: string, timeFormat?: string): string {
  const datePart = getDateFormatString(dateFormat);
  if (!timeFormat) {
    return datePart;
  }
  const timePart = getTimeFormat(timeFormat);
  return `${datePart} ${timePart}`;
}

function getDateOnlyFormat(dateFormat: string): string {
  const formats: Record<string, string> = {
    'MM/DD/YYYY': 'MM/dd/yyyy',
    'DD/MM/YYYY': 'dd/MM/yyyy',
    'YYYY-MM-DD': 'yyyy-MM-dd',
    'MMM DD, YYYY': 'MMM dd, yyyy',
  };
  return formats[dateFormat] || 'MM/dd/yyyy';
}

function getTimeOnlyFormat(timeFormat: string): string {
  if (timeFormat === '24h') {
    return 'HH:mm';
  }
  return 'h:mm a';
}

function buildFormatString(dateFormat: string, timeFormat: string): string {
  const datePart = getDateOnlyFormat(dateFormat);
  const timePart = getTimeOnlyFormat(timeFormat);
  return `${datePart} ${timePart}`;
}

// ========================================
// Timezone Conversion Functions
// ========================================

export function toTimezone(utcDate: Date | string, timezone: string): DateTime {
  const dt =
    typeof utcDate === 'string'
      ? DateTime.fromISO(utcDate, { zone: 'UTC' })
      : DateTime.fromJSDate(utcDate, { zone: 'UTC' });

  return dt.setZone(timezone);
}

export function toUTC(localDate: Date | string, timezone: string): Date {
  const dt =
    typeof localDate === 'string'
      ? DateTime.fromISO(localDate, { zone: timezone })
      : DateTime.fromJSDate(localDate, { zone: timezone });

  return dt.toUTC().toJSDate();
}

export function toISOInTimezone(date: Date | string, timezone: string): string {
  return toTimezone(date, timezone).toISO() || '';
}

export function isValidTimezone(timezone: string): boolean {
  try {
    DateTime.now().setZone(timezone);
    return true;
  } catch {
    return false;
  }
}

// ========================================
// Formatting Functions (using DateTimeSettings)
// ========================================

export function format(
  date: Date | string,
  settings: DateTimeSettings,
  includeTime = true
): string {
  const dt = toTimezone(date, settings.timezone);
  const formatStr = getFormatString(
    settings.dateFormat,
    includeTime ? '12h' : undefined
  );
  return dt.toFormat(formatStr);
}

export function formatDate(
  date: Date | string,
  settings: DateTimeSettings
): string {
  return format(date, settings, false);
}

export function formatTime(date: Date | string, timezone: string): string {
  const dt = toTimezone(date, timezone);
  return dt.toFormat(getTimeFormat('12h'));
}

export function formatForGrid(
  value: Date | string | null | undefined,
  settings: DateTimeSettings,
  includeTime = true
): string {
  if (!value) return '';
  return format(value, settings, includeTime);
}

// ========================================
// Formatting Functions (explicit parameters)
// ========================================

export function formatDateTime(
  date: Date | string,
  timezone: string,
  dateFormat: string,
  timeFormat: string
): string {
  const dt = toTimezone(date, timezone);
  const formatStr = buildFormatString(dateFormat, timeFormat);
  return dt.toFormat(formatStr);
}

export function formatDateOnly(
  date: Date | string,
  timezone: string,
  dateFormat: string
): string {
  const dt = toTimezone(date, timezone);
  const formatStr = getDateOnlyFormat(dateFormat);
  return dt.toFormat(formatStr);
}

export function formatTimeOnly(
  date: Date | string,
  timezone: string,
  timeFormat: string
): string {
  const dt = toTimezone(date, timezone);
  const formatStr = getTimeOnlyFormat(timeFormat);
  return dt.toFormat(formatStr);
}

// ========================================
// Utility Functions
// ========================================

export function getDateOffsetInDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

export function formatDuration(
  milliseconds: number | null | undefined
): string {
  if (milliseconds === null || milliseconds === undefined) return '';
  if (Number.isNaN(milliseconds) || milliseconds < 0) return '';

  const totalMinutes = Math.floor(milliseconds / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export function formatAbsoluteDateTime(value: string | Date): string {
  if (!value) return '';

  const date =
    typeof value === 'string'
      ? DateTime.fromISO(value)
      : DateTime.fromJSDate(value);
  return date.toFormat('MM/dd/yyyy - hh:mm a');
}

export function formatTimeAgo(
  value: string | Date,
  showSeconds = false
): string {
  if (!value) return '';

  const date =
    typeof value === 'string'
      ? DateTime.fromISO(value)
      : DateTime.fromJSDate(value);
  const now = DateTime.now();

  const diff = now.diff(date, ['days', 'hours', 'minutes', 'seconds']);
  const days = Math.floor(diff.days);
  const totalHours = diff.hours;
  const hours = Math.floor(diff.hours % 24);
  const minutes = Math.floor(diff.minutes % 60);
  const seconds = Math.floor(diff.seconds % 60);

  if (days > 14) {
    return date.toFormat('MM/dd/yyyy - hh:mm a');
  }

  if (showSeconds && days === 0 && totalHours === 0 && minutes === 0) {
    if (seconds < 15) {
      return 'Just now';
    }
    return `${seconds} sec ago`;
  }

  if (days === 0 && totalHours === 0 && minutes === 0) {
    return 'Just now';
  }

  if (days === 0 && totalHours < 1) {
    return `${minutes} min ago`;
  }

  if (days === 0) {
    const roundedHours = minutes >= 30 ? hours + 1 : hours;
    return `${roundedHours} ${roundedHours !== 1 ? 'hrs' : 'hr'} ago`;
  }

  const roundedHours = minutes >= 30 ? hours + 1 : hours;
  return `${days} day${days !== 1 ? 's' : ''} ${roundedHours} ${roundedHours !== 1 ? 'hrs' : 'hr'} ago`;
}

// ========================================
// DateTimeService class (backwards-compatible wrapper)
// ========================================

export class DateTimeService {
  toTimezone(utcDate: Date | string, timezone: string): DateTime {
    return toTimezone(utcDate, timezone);
  }

  toUTC(localDate: Date | string, timezone: string): Date {
    return toUTC(localDate, timezone);
  }

  toISOInTimezone(date: Date | string, timezone: string): string {
    return toISOInTimezone(date, timezone);
  }

  isValidTimezone(timezone: string): boolean {
    return isValidTimezone(timezone);
  }

  format(
    date: Date | string,
    settings: DateTimeSettings,
    includeTime = true
  ): string {
    return format(date, settings, includeTime);
  }

  formatDate(date: Date | string, settings: DateTimeSettings): string {
    return formatDate(date, settings);
  }

  formatTime(date: Date | string, timezone: string): string {
    return formatTime(date, timezone);
  }

  formatForGrid(
    value: Date | string | null | undefined,
    settings: DateTimeSettings,
    includeTime = true
  ): string {
    return formatForGrid(value, settings, includeTime);
  }

  formatDateTime(
    date: Date | string,
    timezone: string,
    dateFormat: string,
    timeFormat: string
  ): string {
    return formatDateTime(date, timezone, dateFormat, timeFormat);
  }

  formatDateOnly(
    date: Date | string,
    timezone: string,
    dateFormat: string
  ): string {
    return formatDateOnly(date, timezone, dateFormat);
  }

  formatTimeOnly(
    date: Date | string,
    timezone: string,
    timeFormat: string
  ): string {
    return formatTimeOnly(date, timezone, timeFormat);
  }

  static getDateOffsetInDays(days: number): string {
    return getDateOffsetInDays(days);
  }

  static formatDuration(milliseconds: number | null | undefined): string {
    return formatDuration(milliseconds);
  }

  static formatAbsoluteDateTime(value: string | Date): string {
    return formatAbsoluteDateTime(value);
  }

  static formatTimeAgo(value: string | Date, showSeconds = false): string {
    return formatTimeAgo(value, showSeconds);
  }
}
